import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect } from 'react';

import { useProfileSummary, useAdminAgentProgress } from '@/features/agent-progress/progressHooks';
import {
  useUploaderProfileSummary,
  useAdminUploaderProgress,
} from '@/features/uploader-progress/progressHooks';
import type { ProgressSummary } from '@/features/agent-progress/tierConstants';
import type { UploaderProgressSummary } from '@/features/uploader-progress/tierConstants';
import {
  SectionToday,
  SectionTierJourney,
  SectionAchievements,
  SectionLast7Days,
} from './ProgressModalSections';
import { useT } from '@/i18n';

export interface ProgressModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'self' | 'admin';
  /** Cluster N v0.6.0 — defaults to 'agent' for backwards-compat with all
   *  Cluster M call sites. Pass 'uploader' to read uploader profile
   *  data instead. Sections render unchanged via a shape adapter that
   *  maps uploader fields (uploadsToday/uploadsMonth/uploads/totalUploads)
   *  back to agent field names — numbers are correct, labels stay generic. */
  audience?: 'agent' | 'uploader';
  /** Used by admin mode. Naming kept generic (agentId) for backwards compat;
   *  for audience='uploader' this is the uploader user id. */
  agentId?: number;
}

/**
 * Cluster M v0.5.0 — "Mi Progreso" 4-section modal. Cluster N v0.6.0
 * extends with `audience` prop so the same component serves both agents
 * and uploaders without duplicating the 4 sections.
 *
 * Self mode + audience='agent'    → /agents/me/profile-summary
 * Self mode + audience='uploader' → /uploader/me/profile-summary
 * Admin mode + audience='agent'   → /admin/agents/:id/profile-summary
 * Admin mode + audience='uploader'→ /admin/uploaders/:id/profile-summary
 */
export function ProgressModal({ open, onClose, mode, audience = 'agent', agentId }: ProgressModalProps) {
  const t = useT();

  const agentSelf  = useProfileSummary();
  const agentAdmin = useAdminAgentProgress(agentId, { enabled: audience === 'agent' && mode === 'admin' && agentId != null });
  const uploaderSelf  = useUploaderProfileSummary();
  const uploaderAdmin = useAdminUploaderProgress(agentId, { enabled: audience === 'uploader' && mode === 'admin' && agentId != null });

  const rawQuery =
    audience === 'uploader'
      ? mode === 'admin' ? uploaderAdmin : uploaderSelf
      : mode === 'admin' ? agentAdmin    : agentSelf;

  // Map uploader response → agent shape so the 4 sections work unchanged.
  // pendingValidation={0,0} suppresses the agent-only "validated note" block.
  const data: ProgressSummary | undefined =
    audience === 'uploader' && rawQuery.data
      ? toAgentShape(rawQuery.data as UploaderProgressSummary)
      : (rawQuery.data as ProgressSummary | undefined);
  const query = { ...rawQuery, data };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="progress-modal-title"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
            className="absolute inset-0 bg-nav-navy/60 cursor-default"
          />
          <motion.div
            initial={{ scale: 0.96, y: 8, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 24 }}
            className="relative bg-white rounded-2xl shadow-elevated w-full max-w-3xl max-h-[85vh] overflow-y-auto font-body text-nav-navy"
          >
            <header className="sticky top-0 bg-white border-b border-nav-card px-6 py-4 flex items-center justify-between z-10">
              <h2 id="progress-modal-title" className="font-head text-[17px] font-bold text-nav-navy">
                {mode === 'admin' && query.data
                  ? `${t('progress.modal.title')} · ${query.data.displayName}`
                  : t('progress.modal.title')}
              </h2>
              <button onClick={onClose} aria-label={t('common.close')} className="p-1 rounded hover:bg-nav-hair">
                <X className="w-5 h-5 text-nav-navy" />
              </button>
            </header>

            {query.isLoading ? (
              <div className="p-8 text-center text-nav-slate">{t('common.loading')}</div>
            ) : query.data ? (
              <div className="divide-y divide-nav-hair">
                <SectionToday        data={query.data} />
                <SectionTierJourney  data={query.data} />
                <SectionAchievements data={query.data} />
                <SectionLast7Days    data={query.data} />
              </div>
            ) : (
              <div className="p-8 text-center text-nav-red">{t('common.error')}</div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Cluster N v0.6.0 — adapter that maps an UploaderProgressSummary into the
 * agent ProgressSummary shape so the 4 reusable sections render correctly
 * without per-audience branches inside each section. Numbers are correct;
 * labels (rendered by sections via i18n) stay generic ("today", "next tier",
 * "this month") — they read naturally for both agents and uploaders.
 *
 * pendingValidation is hardcoded to {0,0} because uploaders have no
 * equivalent concept (the agent's "sub-screening pending uploader" block
 * doesn't apply). The conditional renders inside SectionToday +
 * SectionTierJourney correctly hide when both counts are 0.
 */
function toAgentShape(u: UploaderProgressSummary): ProgressSummary {
  return {
    agentId: u.uploaderId,
    displayName: u.displayName,
    today: {
      nyDate: u.today.nyDate,
      screeningsToday: u.today.uploadsToday,
      targetToday: u.today.targetToday,
      hitTarget: u.today.hitTarget,
    },
    monthly: {
      periodYear: u.monthly.periodYear,
      periodMonth: u.monthly.periodMonth,
      screeningsMonth: u.monthly.uploadsMonth,
      primaryScreeningsMonth: u.monthly.primaryUploadsMonth,
      subScreeningsMonth: u.monthly.subUploadsMonth,
      tierAchievedThisMonth: u.monthly.tierAchievedThisMonth,
      tierMaintained: u.monthly.tierMaintained,
      tierDisplayed: u.monthly.tierDisplayed,
      nextTier: u.monthly.nextTier,
      nextTierThreshold: u.monthly.nextTierThreshold,
      screeningsToNextTier: u.monthly.uploadsToNextTier,
      progressPctToNextTier: u.monthly.progressPctToNextTier,
    },
    streak: { ...u.streak },
    achievements: {
      diamanteDeConstancia: u.achievements.diamanteDeConstancia,
      monthsAtBronceOrAbove: u.achievements.monthsAtBronceOrAbove,
      bestMonth: u.achievements.bestMonth
        ? {
            periodYear: u.achievements.bestMonth.periodYear,
            periodMonth: u.achievements.bestMonth.periodMonth,
            totalScreenings: u.achievements.bestMonth.totalUploads,
          }
        : null,
    },
    last7Days: u.last7Days.map((d) => ({
      nyDate: d.nyDate,
      screenings: d.uploads,
      hitTarget: d.hitTarget,
      isWorkingDay: d.isWorkingDay,
    })),
    history12: u.history12.map((m) => ({
      periodYear: m.periodYear,
      periodMonth: m.periodMonth,
      totalScreenings: m.totalUploads,
      tierReached: m.tierReached,
    })),
    pendingValidation: { todayCount: 0, monthCount: 0 },
  };
}
